import React from 'react';
import { Link } from 'react-router-dom';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-emerald-50 to-orange-50">
      <header className="bg-black text-white">
        <nav className="max-w-7xl mx-auto px-4 py-3 flex items-center">
          <div className="flex items-center space-x-6">
            <Link to="/" className="flex items-center">
              <img
                src="https://rentreview.io/wp-content/uploads/2025/05/Rent-Review-Logo.png"
                alt="RentReview"
                className="h-9"
              />
            </Link>
            <Link to="/leave-review" className="text-sm">Leave a Review</Link>
          </div>
        </nav>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-12">
          <div className="space-y-8">
            <div className="border-b pb-8">
              <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
              <div className="text-gray-600">
                <p>Effective Date: 8th August 2025</p>
                <p>Last Updated: 8th August 2025</p>
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 mb-4">
                RentReview ("we", "us", "our") respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, store, and share your information when you use our website or submit reviews.
              </p>
              <p className="text-gray-700 mb-6">
                By using our website, you agree to this Privacy Policy. If you do not agree, please do not use our services.
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">1. Who We Are</h2>
              <p className="text-gray-700 mb-2">Business Name: Profect Media Ltd Trading as Rent.Reviews</p>
              <p className="text-gray-700 mb-2">Registered Address: 128 City Road, London, EC1V 2NJ</p>
              <p className="text-gray-700 mb-4">Email: info@rentreview.io</p>
              <p className="text-gray-700 mb-4">
                We are the data controller of your personal information for the purposes of the UK General Data Protection Regulation ("UK GDPR") and the Data Protection Act 2018.
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">2. What Data We Collect</h2>
              <p className="text-gray-700 mb-4">When you use RentReview, we may collect:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Identity Data:</strong> Your name (or chosen display name)</li>
                <li><strong>Contact Data:</strong> Your email address, phone number</li>
                <li><strong>Review Data:</strong> Property address (if provided), tenancy dates, landlord/letting agent names, ratings, written reviews, uploaded images</li>
                <li><strong>Technical Data:</strong> IP address, browser type, device info, access times, cookies</li>
                <li><strong>Communication Data:</strong> Messages and correspondence you send to us</li>
              </ul>
              <p className="text-gray-700 mt-4">
                You can choose to submit reviews anonymously by selecting that option during submission.
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">3. How We Use Your Data</h2>
              <p className="text-gray-700 mb-4">We process your data to:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Publish and display reviews on our platform</li>
                <li>Verify the authenticity of reviews</li>
                <li>Contact you about your review or account</li>
                <li>Notify landlords, letting agents, housing associations, or property owners of reviews concerning them</li>
                <li>Moderate and remove unlawful, abusive, or discriminatory content (including AI-assisted moderation)</li>
                <li>Improve our website, services, and user experience</li>
                <li>Send marketing updates and offers if you have given your explicit consent</li>
              </ul>

              <h2 className="text-2xl font-bold mt-8 mb-4">4. Lawful Bases for Processing</h2>
              <p className="text-gray-700">
                We process your personal data under the following legal bases:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mt-4">
                <li><strong>Consent:</strong> When you choose to receive marketing or display your name publicly</li>
                <li><strong>Legitimate Interests:</strong> To operate our platform, moderate reviews, and notify landlords/agents of reviews</li>
                <li><strong>Legal Obligation:</strong> To comply with applicable law or respond to lawful requests</li>
              </ul>

              <h2 className="text-2xl font-bold mt-8 mb-4">5. Sharing Your Data</h2>
              <p className="text-gray-700 mb-4">We may share your personal data with:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Landlords, letting agents, housing associations, or property owners mentioned in your review</li>
                <li>IT service providers who host, maintain, or support our website</li>
                <li>Regulatory or legal authorities when required by law</li>
              </ul>
              <p className="text-gray-700 mt-4">
                We do not sell your personal data to third parties for marketing unless you have given explicit opt-in consent at the time of collection.
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">6. International Transfers</h2>
              <p className="text-gray-700">
                If we transfer your personal data outside the UK, we ensure it is protected by appropriate safeguards such as adequacy decisions or standard contractual clauses.
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">7. Data Retention</h2>
              <p className="text-gray-700 mb-2">
                We will only keep your personal data for as long as necessary to fulfil the purposes we collected it for, including legal, accounting, or reporting requirements.
              </p>
              <p className="text-gray-700">
                Reviews may remain visible on our site unless you request removal.
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">8. Your Rights</h2>
              <p className="text-gray-700 mb-4">You have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Access your personal data</li>
                <li>Correct inaccuracies</li>
                <li>Request deletion</li>
                <li>Restrict processing</li>
                <li>Object to certain uses</li>
                <li>Request data portability</li>
                <li>Withdraw consent at any time</li>
              </ul>
              <p className="text-gray-700 mt-4">
                To exercise these rights, email info@rentreview.io.
              </p>
              <p className="text-gray-700">
                You also have the right to complain to the Information Commissioner's Office (ICO) at www.ico.org.uk.
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">9. Cookies</h2>
              <p className="text-gray-700">
                We use cookies for functionality, analytics, and security. See our{' '}
                <Link to="/cookies-policy" className="text-blue-600 hover:text-blue-800 underline">
                  Cookies Policy
                </Link>
                {' '}for details.
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">10. Security</h2>
              <p className="text-gray-700">
                We use appropriate technical and organisational measures to protect your data. However, no system is completely secure, and we cannot guarantee 100% protection.
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">11. Changes to This Policy</h2>
              <p className="text-gray-700">
                We may update this Privacy Policy from time to time. We will notify you by updating the "Last Updated" date and, where appropriate, by email.
              </p>

              <h2 className="text-2xl font-bold mt-8 mb-4">Contact Us:</h2>
              <p className="text-gray-700">
                Email: <a href="mailto:info@rentreview.io" className="text-blue-600 hover:text-blue-800">info@rentreview.io</a>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}